import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AdminHttpClient } from '../http.js';
import type { AdminAuditEvent } from '../types.js';
import { DataTable } from '../components/DataTable.js';

export function AuditScreen({ http }: { http: AdminHttpClient }) {
  const { t } = useTranslation();
  const [events, setEvents] = useState<AdminAuditEvent[]>([]);
  const [actor, setActor] = useState('');
  const [target, setTarget] = useState('');
  const [eventType, setEventType] = useState('');

  useEffect(() => {
    void http.listAudit().then(setEvents);
  }, [http]);

  const filtered = useMemo(
    () =>
      events.filter(
        (event) =>
          (!actor || event.actorUserId.includes(actor)) &&
          (!target || event.targetId.includes(target)) &&
          (!eventType || event.eventType.includes(eventType))
      ),
    [events, actor, target, eventType]
  );

  function exportJson() {
    const blob = new Blob([JSON.stringify(filtered, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'veluga-admin-audit.json';
    anchor.click();
    URL.revokeObjectURL(url);
  }

  return (
    <section className="content-stack">
      <p className="screen-help">{t('screenHelp.audit')}</p>
      <div className="toolbar-form">
        <input placeholder={t('audit.actor')} value={actor} onChange={(event) => setActor(event.target.value)} />
        <input placeholder={t('audit.target')} value={target} onChange={(event) => setTarget(event.target.value)} />
        <input placeholder={t('audit.eventType')} value={eventType} onChange={(event) => setEventType(event.target.value)} />
        <button onClick={exportJson}>{t('audit.exportJson')}</button>
      </div>
      {/* Filtering stays in the screen-level inputs above so Export JSON keeps
          exporting EVERY filtered row, not just the visible page. */}
      <DataTable
        rows={filtered}
        empty={t('audit.noEvents')}
        pageSize={50}
        defaultSort={{ key: 'created', dir: 'desc' }}
        getRowKey={(event) => event.id}
        columns={[
          {
            key: 'created',
            header: t('audit.created'),
            render: (event) => new Date(event.createdAt).toLocaleString(),
            // Audit ids are append-monotonic (`aud_<seq>`), so the seq alone
            // gives exact creation order even when same-ms timestamps collide.
            sortValue: (event) => Number(event.id.slice(4))
          },
          { key: 'type', header: t('audit.event'), render: (event) => event.eventType },
          { key: 'target', header: t('audit.target'), render: (event) => `${event.targetType}:${event.targetId}` },
          { key: 'id', header: t('audit.auditId'), render: (event) => <code>{event.id}</code> }
        ]}
      />
    </section>
  );
}
